import * as React from 'react';
import styled from 'styled-components';
import {TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import cssConstants from '../../../constants/cssConstants';
import {IMediaAnalyticsModel} from '../../../model/mediaAnalyticsModel';
import {IConversationData} from '../../../model/conversationModel';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {conversationsStore} from '../../../context/conversationsProvider';
import {useTscTheme} from '../../../context/themeProvider';
import {mediaStore} from '../../../context/mediaProvider';

export const AnalyticsSegmentWrapper = styled.div`
   display: flex;
   justify-content: space-between;
   margin: 0;
   padding: 0;

   @media screen and (max-width: ${cssConstants.tabBreakpoint}px) {
      justify-content: center;
      flex-direction: column;
   }
`;

const SegmentDiv = styled.li<{theme: TscThemeNames}>`
   padding: 0.25rem;
   display: flex;
   text-align: center;
   flex-basis: 25%;
   height: 5rem;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   color: ${prop => prop.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.medium};
   flex-grow: 1;
`;

const SegmentHalf = styled.ul<{theme: TscThemeNames}>`
   display: flex;
   flex: 1 0 50%;
   justify-content: space-evenly;
   padding: 0;
   list-style-type: none;
   @media screen and (max-width: ${cssConstants.tabBreakpoint}px) {
      flex-basis: auto;

      &:first-child {
         border-bottom: 1px solid ${prop => prop.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.dark};
      }
   }
`;

const SegmentData = styled.div`
   font-size: 2rem;
`;

const SegmentLabel = styled.div`
   font-weight: 600;
   font-size: 0.875rem;
`;

const SegmentDivWithRightBorder = styled(SegmentDiv)`
   border-right: 1px solid ${prop => prop.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.dark};
`;

const SegmentDivWithWidthDependentBorder = styled(SegmentDiv)`
   border-right: 1px solid ${prop => prop.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.dark};

   @media screen and (max-width: ${cssConstants.tabBreakpoint}px) {
      border-right: none;
   }
`;

export const AnalyticsSegmentDataBase: React.FC<IAnalyticsSegmentDataProps & IStateMappedProps> = ({analytics, conversations, ViewCount}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();

   const getCompletionRate = (): number => {
      const numOfViewers = analytics.length;
      const listOfViewPercentages = analytics.map(a => a.PercentViewed);
      const sumOfViewPercentages = listOfViewPercentages.reduce((total, currentVal) => total + currentVal, 0);

      return Math.round(((sumOfViewPercentages / numOfViewers) * 1000) / 10) || 0;
   };

   const getCommentsCount = (): number => {
      const commentCounts = conversations && conversations.map(conversation => conversation.CommentCount);
      return commentCounts ? commentCounts.reduce((total, currentVal) => total + currentVal, 0) : 0;
   };

   return (
      <AnalyticsSegmentWrapper data-test-id="analytics-segment-wrapper">
         <SegmentHalf theme={theme}>
            <SegmentDivWithRightBorder theme={theme}>
               <SegmentData>
                  {ViewCount}
               </SegmentData>
               <SegmentLabel>
                  {t('analytics.viewsLabel')}
               </SegmentLabel>
            </SegmentDivWithRightBorder>
            <SegmentDivWithWidthDependentBorder theme={theme}>
               <SegmentData>
                  {analytics.length}
               </SegmentData>
               <SegmentLabel>
                  {t('analytics.viewersLabel')}
               </SegmentLabel>
            </SegmentDivWithWidthDependentBorder>
         </SegmentHalf>
         <SegmentHalf theme={theme}>
            <SegmentDivWithRightBorder theme={theme}>
               <SegmentData>
                  {getCommentsCount()}
               </SegmentData>
               <SegmentLabel>
                  {t('analytics.commentsLabel')}
               </SegmentLabel>
            </SegmentDivWithRightBorder>
            <SegmentDiv theme={theme}>
               <SegmentData>
                  {getCompletionRate()}
                  %
               </SegmentData>
               <SegmentLabel>
                  {t('analytics.completionRateLabel')}
               </SegmentLabel>
            </SegmentDiv>
         </SegmentHalf>
      </AnalyticsSegmentWrapper>
   );
};

export interface IAnalyticsSegmentDataProps {
   analytics: IMediaAnalyticsModel[];
}

export interface IStateMappedProps {
   conversations: IConversationData[];
   ViewCount: number;
}

const mapStatesToProps = (
   {analytics}: IAnalyticsSegmentDataProps,
   {conversations}: Partial<IStateMappedProps>,
   {ViewCount}: Partial<IStateMappedProps>
): IAnalyticsSegmentDataProps & IStateMappedProps => ({analytics, conversations, ViewCount});

export default withMemoizedContexts(mapStatesToProps, conversationsStore, mediaStore)(AnalyticsSegmentDataBase);
